import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        primary:
          "bg-[var(--cl-ink)] text-white font-semibold rounded-[60px] hover:bg-[#2A2A2E] transition-colors duration-200 ease-in-out justify-between text:md",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border-[1.5px] border-[var(--cl-ink)] bg-transparent text-[var(--cl-ink)] rounded-[60px] hover:bg-[var(--cl-ink)] hover:text-white transition-colors duration-200 ease-in-out",
        secondary:
          "bg-[var(--cl-ink)] text-white rounded-[60px] border-[1.5px] border-[var(--cl-ink)] hover:bg-[#2A2A2E] transition-colors duration-200 ease-in-out",
        "regular-outline-black":
          "border-[1.5px] border-[var(--cl-ink)] bg-transparent text-[var(--cl-ink)] rounded-[60px] hover:bg-[var(--cl-ink)] hover:text-white transition-colors duration-200 ease-in-out",
        "regular-outline-red":
          "border-[1.5px] border-[var(--cl-ink)] bg-transparent text-[var(--cl-ink)] rounded-[60px] hover:bg-[var(--cl-ink)] hover:text-white transition-colors duration-200 ease-in-out",
        "subscribe":
          "border-[1.5px] border-[var(--cl-ink)] bg-transparent text-[var(--cl-ink)] rounded-[60px] font-semibold hover:bg-[var(--cl-ink)] hover:text-white transition-colors duration-200 ease-in-out justify-between text-md",
        "action":
          "border-[1.5px] border-[var(--cl-ink)] bg-transparent text-[var(--cl-ink)] rounded-[60px] font-semibold hover:bg-[var(--cl-ink)] hover:text-white transition-colors duration-200 ease-in-out justify-between min-w-[300px]",
        "sign-in":
          "bg-transparent text-[var(--cl-ink)] rounded-[60px] border-[1px] border-[#DCDCD6] font-normal hover:bg-[#EFEFEC] transition-colors duration-200 ease-in-out",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        "arrow-link":
          "!h-auto !p-0 rounded-none bg-transparent text-[var(--cl-ink)] font-semibold gap-1.5 underline-offset-4 hover:underline hover:gap-2.5 [&_svg]:size-4",
      },
      size: {
        default: "h-12 px-6 py-3",
        sm: "h-10 rounded-[60px] px-5 text-xs",
        lg: "h-14 rounded-[60px] px-8 text-base",
        icon: "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    (<Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />)
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
